import { useLayoutEffect, useRef, useState } from "react";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import type { ProductCoa } from "@models/product";
import { asApiFailure } from "@utils/apiFailure";
import { saveBlob } from "@utils/fileDownload";
import { formatDate } from "@utils/formatter";
import {
    ProductCoaField,
    type CoaNotice,
} from "../../_components/ProductCoaField";
import { productCoaError } from "../../_helpers/productCoaFile";

interface ProductCoaManagerProps {
    productId: string;
    /** The product's stored COA, as the last product read returned it. */
    coa: ProductCoa | null;
    /** Re-fetch the product so the row shows the stored COA after a change. */
    onChanged: () => void;
    /** Locks Attach / Replace and Remove (processing, loading, inactive). The download stays. */
    disabled?: boolean;
}

// The last action's state, for the product it was started on.
interface CoaState {
    productId: string;
    busy: boolean;
    notice: CoaNotice | null;
}

// The Edit Product page's COA (#476, DecisionLog I3). Attach, Replace and
// Remove save at once, as the image controls do, then re-read the product;
// the file name downloads the COA. The row always shows the COA the API last
// reported, never the file being sent, so a failed attach or remove never
// looks done. Every action says it is in flight, then that it succeeded or
// failed.
// The route re-uses this mount across products: an action is reported only
// on the product it was started on, and one that lands after the page moved
// on (or unmounted) reports nothing, saves nothing and re-reads nothing. The
// re-read goes through the page's latest onChanged, not the one in hand when
// the action started: after the route leaves the product and comes back, the
// page refuses that one.
export const ProductCoaManager = ({
    productId,
    coa,
    onChanged,
    disabled = false,
}: ProductCoaManagerProps) => {
    const { uploadCoa, removeCoa, downloadCoa } = useProductApi();
    const [state, setState] = useState<CoaState | null>(null);

    // Another product starts idle, in the same render.
    if (state !== null && state.productId !== productId) {
        setState(null);
    }

    // The product shown now; null once unmounted.
    const shownProductId = useRef<string | null>(productId);
    useLayoutEffect(() => {
        shownProductId.current = productId;

        return () => {
            shownProductId.current = null;
        };
    }, [productId]);

    // The page's refresh as of the last render. The one in hand when an
    // action started belongs to that render's read; after p1 → p2 → p1 the
    // page has re-read p1 and refuses it (PR 422 review 1).
    const onChangedRef = useRef(onChanged);
    useLayoutEffect(() => {
        onChangedRef.current = onChanged;
    });

    // `reread`: the API's answer means the stored COA differs from the row,
    // so the product is read again.
    const settle = (
        startedOn: string,
        notice: CoaNotice | null,
        reread = false,
    ) => {
        if (shownProductId.current === startedOn) {
            if (reread) {
                onChangedRef.current();
            }
            setState({ productId: startedOn, busy: false, notice });
        }
    };

    const attach = async (file: File) => {
        const refusal = productCoaError(file);

        if (refusal) {
            setState({
                productId,
                busy: false,
                notice: { tone: "error", text: refusal },
            });
            return;
        }

        setState({
            productId,
            busy: true,
            notice: { tone: "status", text: `Attaching ${file.name}…` },
        });

        // A 409 the API wrote: another admin attached or removed this
        // product's COA between this upload's read and its save. Answered
        // here, so useApi's toast (which would say to reload) is silenced for
        // it alone; a body-less 409 is not the API's and keeps the toast.
        let isConflict = false;

        try {
            await uploadCoa(productId, file, {
                skipErrorNotification: (status, _errorCode, hasApiBody) => {
                    isConflict = status === 409 && hasApiBody;
                    return isConflict;
                },
            });
        } catch {
            // useApi has toasted every other failure it could; the inline
            // line says, for all of them, that nothing was attached. After a
            // conflict the stored COA changed: the re-read shows the current
            // one.
            settle(
                productId,
                {
                    tone: "error",
                    text: isConflict
                        ? `${file.name} was not attached: another admin changed this product's COA at the same moment. The COA shown is the current one.`
                        : `${file.name} was not attached.`,
                },
                isConflict,
            );
            return;
        }

        settle(
            productId,
            { tone: "status", text: `${file.name} attached.` },
            true,
        );
    };

    const remove = async () => {
        setState({
            productId,
            busy: true,
            notice: { tone: "status", text: "Removing the COA…" },
        });

        try {
            await removeCoa(productId);
        } catch {
            settle(productId, {
                tone: "error",
                text: "The COA was not removed.",
            });
            return;
        }

        settle(productId, { tone: "status", text: "COA removed." }, true);
    };

    const download = async (stored: ProductCoa) => {
        setState({
            productId,
            busy: true,
            notice: {
                tone: "status",
                text: `Downloading ${stored.fileName}…`,
            },
        });

        try {
            const downloaded = await downloadCoa(productId);

            if (shownProductId.current !== productId) {
                return;
            }

            // The server's Content-Disposition name wins.
            saveBlob(downloaded.blob, downloaded.fileName ?? stored.fileName);
            settle(productId, null);
        } catch (error) {
            // The API's own 404: no COA any more, or its file is gone. The
            // re-read stops the row offering it.
            const isGone = asApiFailure(error)?.status === 404;
            settle(
                productId,
                {
                    tone: "error",
                    text: isGone
                        ? "This COA is no longer available."
                        : "Couldn't download the COA.",
                },
                isGone,
            );
        }
    };

    return (
        <ProductCoaField
            fileName={coa?.fileName ?? null}
            detail={coa ? `Updated ${formatDate(coa.updatedAt)}` : undefined}
            onDownload={coa ? () => void download(coa) : undefined}
            onPick={(file) => void attach(file)}
            onRemove={() => void remove()}
            disabled={disabled}
            busy={state?.busy ?? false}
            notice={state?.notice ?? null}
        />
    );
};
